import { useState, useRef, useEffect } from 'react';
import { AppShell } from '../../components/layout/AppShell';
import { Card } from '../../components/ui/Card';
import { Table } from '../../components/ui/Table';
import { Select } from '../../components/ui/Select';
import { Combobox } from '../../components/ui/Combobox';
import { Spinner } from '../../components/ui/Spinner';
import { Alert } from '../../components/ui/Alert';
import { EmptyState } from '../../components/ui/EmptyState';
import { KpiCard } from '../../components/kpi/KpiCard';
import { MultiLineChart } from '../../components/charts/MultiLineChart';
import { DriverBars } from '../../components/charts/DriverBars';
import { ProductPicker } from '../../components/charts/ProductPicker';
import { FilterBar } from '../../components/filters/FilterBar';
import { PeriodToggle } from '../../components/filters/PeriodToggle';
import { RangeFilters } from '../../components/filters/RangeFilters';
import { useApiData } from '../../hooks/useApiData';
import { useOptions } from '../../hooks/useOptions';
import { formatNumber, formatPercent } from '../../utils/formatters';
import { csvCell, downloadText } from '../../utils/exportCsv';
import { exportPdf } from '../../utils/exportPdf';
import { colorForProduct } from '../../config/productColors';
import { dayTickLabel } from '../../utils/periods';

const sanitize = (s) => String(s).replace(/[^\w-]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');

const PERIOD_LABEL = { day: 'Daily', month: 'Monthly', year: 'Yearly' };

// Overview trend that sits ABOVE a breakdown table: one line per row-entity (top ~6 by
// total) over time, so the table's snapshot ranking is paired with each entity's trajectory.
// `dimOf(d)` maps a tidy `detail` row to its entity (a product's family, or the reseller).
function OverviewTrend({ detail, dimOf, granularity }) {
  const totals = {};
  const rowsMap = new Map();
  (detail || []).forEach((d) => {
    const k = dimOf(d);
    if (!k) return;
    totals[k] = (totals[k] || 0) + d.total;
    if (!rowsMap.has(d.period)) rowsMap.set(d.period, { period: d.period, cells: {} });
    const cells = rowsMap.get(d.period).cells;
    cells[k] = (cells[k] || 0) + d.total;
  });
  const keys = Object.keys(totals).sort((a, b) => totals[b] - totals[a]).slice(0, 6);
  if (keys.length < 1 || rowsMap.size < 2) return null;
  const chartData = [...rowsMap.values()]
    .sort((a, b) => (a.period < b.period ? -1 : 1))
    .map((r) => { const row = { period: r.period }; keys.forEach((k) => { row[k] = r.cells[k] || 0; }); return row; });
  const colorFor = {};
  keys.forEach((k) => { colorFor[k] = colorForProduct(k); });
  return (
    <div className="mb-4">
      <MultiLineChart
        data={chartData}
        series={keys}
        colorFor={colorFor}
        height={180}
        xTickFormatter={granularity === 'day' ? dayTickLabel : undefined}
      />
    </div>
  );
}

export default function Reports() {
  const opts = useOptions();
  const [reseller, setReseller] = useState('');
  const [productFamily, setProductFamily] = useState('');
  const [granularity, setGranularity] = useState('day');
  const [range, setRange] = useState({ from: '', to: '', preset: '' });
  const { from, to } = range;
  const [exportOpen, setExportOpen] = useState(false);
  const exportRef = useRef(null);
  const periodLabel = PERIOD_LABEL[granularity] || 'Periodic';

  // Default to the LAST 7 DAYS (Daily grouping) on first load; one-time.
  const initRange = useRef(false);
  useEffect(() => {
    if (!initRange.current && opts.dateRange?.max) {
      const [y, mo, d] = opts.dateRange.max.split('-').map(Number);
      const start = new Date(y, mo - 1, d - 6);
      const pad = (n) => String(n).padStart(2, '0');
      const fromStr = `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`;
      setRange({ from: fromStr, to: opts.dateRange.max, preset: '' });
      initRange.current = true;
    }
  }, [opts.dateRange]);

  useEffect(() => {
    if (!exportOpen) return undefined;
    const onClick = (e) => { if (exportRef.current && !exportRef.current.contains(e.target)) setExportOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [exportOpen]);

  const { data, loading, error } = useApiData('/api/data/reports', {
    reseller, productFamily, granularity, from, to,
  });

  // Usage Over Time as one colour-coded line per product (raw per-day values, summed
  // across resellers). Colours fixed per product; a picker chooses which to draw.
  const detail = data?.detail || [];
  const ppTotals = {};
  const ppRowsMap = new Map();
  detail.forEach((d) => {
    ppTotals[d.product] = (ppTotals[d.product] || 0) + d.total;
    if (!ppRowsMap.has(d.period)) ppRowsMap.set(d.period, { period: d.period, cells: {} });
    const cells = ppRowsMap.get(d.period).cells;
    cells[d.product] = (cells[d.product] || 0) + d.total; // sum across resellers
  });
  const ppProducts = Object.keys(ppTotals).sort((a, b) => ppTotals[b] - ppTotals[a]);
  const chartData = [...ppRowsMap.values()]
    .sort((a, b) => (a.period < b.period ? -1 : 1))
    .map((r) => ({ period: r.period, ...r.cells }));
  const colorFor = {};
  ppProducts.forEach((n) => { colorFor[n] = colorForProduct(n); });
  const [selProducts, setSelProducts] = useState([]);
  const [activeProduct, setActiveProduct] = useState(null);
  const initedKey = useRef(null);
  const viewKey = `${reseller}|${productFamily}`;
  useEffect(() => {
    if (ppProducts.length && initedKey.current !== viewKey) {
      setSelProducts(ppProducts.slice(0, 8));
      initedKey.current = viewKey;
    }
  }, [ppProducts, viewKey]);
  const seriesToDraw = ppProducts.filter((n) => selProducts.includes(n));
  const toggleProduct = (n) => setSelProducts((c) => (c.includes(n) ? c.filter((x) => x !== n) : [...c, n]));

  const baseName = () => {
    const r = reseller ? sanitize(reseller) : 'all-resellers';
    const f = productFamily ? sanitize(productFamily) : 'all-products';
    return `cysops-report_${r}_${f}_${from || 'start'}_${to || 'end'}`;
  };

  // The product breakdown shows individual products when a family is filtered,
  // otherwise the product-family breakdown.
  const productRows = productFamily ? (data?.byProduct || []) : (data?.byFamily || []);
  const productTitle = productFamily ? `Products in ${productFamily}` : 'Usage by Product Family';

  const filterLines = () => [
    `Reseller: ${reseller || 'All resellers'}`,
    `Product family: ${productFamily || 'All products'}`,
    `Grouping: ${granularity}`,
    `Range: ${from || 'start'} to ${to || 'end'}`,
    `Generated: ${new Date().toLocaleString()}`,
  ];

  // Detailed multi-section CSV: filters, summary, products, resellers, time series.
  const doCsv = () => {
    setExportOpen(false);
    if (!data) return;
    const lines = [];
    const row = (arr) => lines.push(arr.map(csvCell).join(','));
    row(['CysOps Usage Report']);
    filterLines().forEach((l) => { const [k, ...v] = l.split(':'); row([k.trim(), v.join(':').trim()]); });
    lines.push('');
    row(['Summary']);
    row(['Total Usage', data.summary.totalUsage]);
    row(['Resellers', data.summary.resellers]);
    row(['Product Families', data.summary.products]);
    lines.push('');
    const section = (title, rows, cols) => {
      row([title]);
      row(cols.map((c) => c.label));
      rows.forEach((r) => row(cols.map((c) => r[c.key])));
      lines.push('');
    };
    const shareCols = [{ key: 'name', label: 'Name' }, { key: 'total', label: 'Usage' }, { key: 'share', label: 'Share %' }];
    section(productTitle, productRows, shareCols);
    section('Usage by Reseller', data.byReseller, shareCols);
    // Detailed breakdown, pivoted into one row per reseller-per-period with a column
    // per product. Rows are grouped by reseller (largest first), and within each
    // reseller ordered by period from first to last.
    const detail = data.detail || [];
    if (detail.length) {
      const prodTotals = {};
      const resTotals = {};
      const byRes = {}; // reseller -> { period -> { total, cells } }
      detail.forEach((d) => {
        prodTotals[d.product] = (prodTotals[d.product] || 0) + d.total;
        resTotals[d.reseller] = (resTotals[d.reseller] || 0) + d.total;
        if (!byRes[d.reseller]) byRes[d.reseller] = {};
        if (!byRes[d.reseller][d.period]) byRes[d.reseller][d.period] = { total: 0, cells: {} };
        byRes[d.reseller][d.period].cells[d.product] = d.total;
        byRes[d.reseller][d.period].total += d.total;
      });
      const products = Object.keys(prodTotals).sort((a, b) => prodTotals[b] - prodTotals[a]);
      const resellers = Object.keys(resTotals).sort((a, b) => resTotals[b] - resTotals[a]);

      row([`${periodLabel} Usage - Reseller x Product (detailed)`]);
      row(['Reseller', 'Period', ...products, 'Total']);
      resellers.forEach((res) => {
        // 'YYYY-MM-DD' / 'YYYY-MM' / 'YYYY' are zero-padded, so a lexical sort is chronological.
        const periods = Object.keys(byRes[res]).sort();
        periods.forEach((pd) => {
          const cell = byRes[res][pd];
          row([res, pd, ...products.map((pn) => cell.cells[pn] ?? 0), cell.total]);
        });
      });
      lines.push('');
    }
    const pr = data.productReseller;
    if (pr && pr.products.length) {
      row([productFamily ? `Reseller x Product - ${productFamily}` : 'Reseller x Product usage']);
      row(['Reseller', ...pr.products, 'Total']);
      pr.rows.forEach((rr) => row([rr.reseller, ...pr.products.map((pn) => rr.cells[pn] || 0), rr.total]));
      lines.push('');
    }
    downloadText(`${baseName()}.csv`, lines.join('\n'), 'text/csv');
  };

  const doPdf = () => {
    setExportOpen(false);
    if (!data) return;
    const shareCols = [
      { key: 'name', label: 'Name' },
      { key: 'total', label: 'Usage', align: 'right', fmt: formatNumber },
      { key: 'share', label: 'Share', align: 'right', fmt: (v) => (v == null ? '—' : `${v}%`) },
    ];
    exportPdf(baseName(), {
      title: 'CysOps — Usage Report',
      filters: filterLines(),
      kpis: [
        { label: 'Total Usage', value: formatNumber(data.summary.totalUsage) },
        { label: 'Resellers', value: formatNumber(data.summary.resellers) },
        { label: 'Product Families', value: formatNumber(data.summary.products) },
      ],
      tables: [
        { heading: productTitle, columns: shareCols, rows: productRows, chart: 'bar' },
        { heading: 'Usage by Reseller', columns: shareCols, rows: data.byReseller, chart: 'bar' },
        ...(productFamily && data.productReseller?.products.length ? [{
          heading: `Reseller × Product — ${productFamily}`,
          columns: [
            { key: 'reseller', label: 'Reseller' },
            ...data.productReseller.products.map((pn) => ({ key: pn, label: pn, align: 'right', fmt: formatNumber })),
            { key: 'total', label: 'Total', align: 'right', fmt: formatNumber },
          ],
          rows: data.productReseller.rows.map((r) => ({ reseller: r.reseller, total: r.total, ...r.cells })),
        }] : []),
      ],
    });
  };

  const breakdownCols = [
    { key: 'name', label: 'Name' },
    { key: 'total', label: 'Usage (avg/day)', align: 'right', render: (v) => formatNumber(v) },
    { key: 'share', label: 'Share', align: 'right', render: (v) => formatPercent(v) },
  ];

  return (
    <AppShell title="Reports — Custom Explorer">
      <FilterBar>
        <div className="w-full sm:w-48">
          <Combobox label="Reseller" emptyOption="All resellers" value={reseller}
            onChange={setReseller} options={opts.resellers} searchPlaceholder="Search reseller…" />
        </div>
        <div className="w-full sm:w-44">
          <Select label="Product family" placeholder="All products" value={productFamily}
            onChange={(e) => setProductFamily(e.target.value)} options={opts.productFamilies} />
        </div>
        <div>
          <span className="block mb-1 text-sm font-medium text-slate-600 dark:text-slate-300">Grouping</span>
          <PeriodToggle value={granularity} onChange={setGranularity} />
        </div>
        <RangeFilters from={from} to={to} preset={range.preset} dateRange={opts.dateRange} onChange={setRange} />
        <div className="ml-auto relative self-end" ref={exportRef}>
          <button
            type="button"
            disabled={!data}
            onClick={() => setExportOpen((v) => !v)}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 dark:border-white/15 bg-white dark:bg-slate-800 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
          >
            Export
            <svg className={`h-4 w-4 text-slate-400 dark:text-slate-500 transition-transform ${exportOpen ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.2 7.2a.75.75 0 011.06.02L10 11.06l3.74-3.84a.75.75 0 111.08 1.04l-4.28 4.4a.75.75 0 01-1.08 0l-4.28-4.4a.75.75 0 01.02-1.06z" clipRule="evenodd" />
            </svg>
          </button>
          {exportOpen && (
            <div className="absolute right-0 z-20 mt-2 w-40 overflow-hidden rounded-lg bg-white dark:bg-slate-800 shadow-xl ring-1 ring-slate-200 dark:ring-white/10">
              <button type="button" onClick={doCsv} className="block w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/10">Export CSV</button>
              <button type="button" onClick={doPdf} className="block w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/10">Export PDF</button>
            </div>
          )}
        </div>
      </FilterBar>

      {loading && <Spinner />}
      {error && <Alert>{error}</Alert>}
      {data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <KpiCard label="Total Usage" value={formatNumber(data.summary.totalUsage)} sub="avg / day" />
            <KpiCard label="Resellers" value={formatNumber(data.summary.resellers)} />
            <KpiCard label="Product Families" value={formatNumber(data.summary.products)} />
          </div>

          <Card
            title="Product Usage Over Time"
            actions={ppProducts.length ? (
              <ProductPicker
                products={ppProducts}
                selected={selProducts}
                colorFor={colorFor}
                onToggle={toggleProduct}
                onSelectAll={() => setSelProducts(ppProducts)}
                onClear={() => setSelProducts([])}
                onHover={setActiveProduct}
              />
            ) : null}
          >
            {chartData.length && seriesToDraw.length ? (
              <MultiLineChart
                data={chartData}
                series={seriesToDraw}
                colorFor={colorFor}
                activeSeries={activeProduct}
                xTickFormatter={granularity === 'day' ? dayTickLabel : undefined}
              />
            ) : <EmptyState />}
          </Card>

          <div className={`grid grid-cols-1 gap-6 ${!reseller ? 'lg:grid-cols-2' : ''}`}>
            <Card title={productTitle}>
              {productRows.length ? (
                <Table
                  columns={breakdownCols}
                  rows={productRows}
                  pageSize={10}
                  {...(!productFamily ? {
                    rowKey: (r) => r.name,
                    expandedContent: (r) => (
                      <DriverBars
                        items={(data.byProduct || []).filter((p) => p.family === r.name).map((p) => ({ name: p.name, value: p.total }))}
                        countLabel="Products"
                        topLabel="Top product"
                        colorOf={(i) => colorForProduct(i.name)}
                      />
                    ),
                  } : {})}
                />
              ) : <EmptyState />}
            </Card>

            {!reseller && (
              <Card title="Top Resellers">
                {data.byReseller && data.byReseller.length ? (
                  <>
                  <OverviewTrend detail={data.detail} dimOf={(d) => d.reseller} granularity={granularity} />
                  <Table
                    columns={breakdownCols}
                    rows={data.byReseller}
                    pageSize={10}
                    rowKey={(r) => r.name}
                    expandedContent={(r) => {
                      const mrow = data.productReseller?.rows.find((x) => x.reseller === r.name);
                      const items = mrow ? Object.entries(mrow.cells).map(([product, value]) => ({ name: product, value })) : [];
                      return <DriverBars items={items} countLabel="Products used" topLabel="Top product" colorOf={(i) => colorForProduct(i.name)} />;
                    }}
                  />
                  </>
                ) : <EmptyState />}
              </Card>
            )}
          </div>

          {productFamily && data.productReseller && data.productReseller.rows.length > 0 && (
            <Card title={`Reseller × Product — ${productFamily}`}>
              <Table
                columns={[
                  { key: 'reseller', label: 'Reseller' },
                  ...data.productReseller.products.map((pn) => ({
                    key: pn, label: pn, align: 'right', render: (v) => formatNumber(v || 0),
                  })),
                  { key: 'total', label: 'Total', align: 'right', render: (v) => formatNumber(v) },
                ]}
                rows={data.productReseller.rows.map((r) => ({ reseller: r.reseller, total: r.total, ...r.cells }))}
                pageSize={10}
                searchable
                searchPlaceholder="Search reseller…"
              />
            </Card>
          )}
        </>
      )}
    </AppShell>
  );
}
